import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import HubIcon from "@mui/icons-material/Hub";
import SaveIcon from "@mui/icons-material/Save";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { wholesaleOrdersApi } from "../../services/api/omni/wholesaleOrders";
import {
  generateWebhookToken,
  toTestWebhookUrl,
} from "../../utils/n8nWorkflowDefaults";

/**
 * n8n parse webhook credentials for wholesale order file upload.
 * Auth: X-API-KEY header (same as Tickets). Test expects sync
 * { action: "test_complete", test_id } when $execution.mode === "test".
 */
const CredentialsTab = ({ s }) => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    webhook_url: "",
    webhook_token: "",
    is_active: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tokenGenerated, setTokenGenerated] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const resolvedToken = () => {
    if (form.webhook_token.trim()) return form.webhook_token.trim();
    return null;
  };

  const canTest =
    (form.webhook_url.trim() || settings?.webhook_url) &&
    (resolvedToken() || settings?.token_set);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await wholesaleOrdersApi.getSettings();
      setSettings(response.data);
      setForm({
        webhook_url: response.data.webhook_url || "",
        webhook_token: "",
        is_active: response.data.is_active ?? true,
      });
      setTokenGenerated(false);
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to load settings",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleGenerateToken = () => {
    setGeneratingToken(true);
    setError(null);
    try {
      const token = generateWebhookToken();
      setForm((prev) => ({ ...prev, webhook_token: token }));
      setShowToken(true);
      setTokenGenerated(true);
      setSuccess(
        "New token generated. Click Save settings to store it — copy it into n8n Header Auth (X-API-KEY).",
      );
    } catch (err) {
      setError(err.message || "Failed to generate token");
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleCopyToken = async () => {
    if (!form.webhook_token) return;
    try {
      await navigator.clipboard.writeText(form.webhook_token);
      setSuccess("Token copied to clipboard.");
    } catch {
      setError("Could not copy to clipboard.");
    }
  };

  const handleTestConnection = async () => {
    const token = resolvedToken();
    const url = form.webhook_url.trim();
    if (!url && !settings?.webhook_url) {
      setError("Webhook URL is required to test.");
      return;
    }
    if (!token && !settings?.token_set) {
      setError(
        "Generate or enter a webhook token and save settings before testing.",
      );
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setSuccess(null);
      setTestResult(null);

      // Omit token when empty so the backend uses the DB-stored secret.
      const payload = {};
      if (url) payload.webhook_url = url;
      if (token) payload.webhook_token = token;

      const response = await wholesaleOrdersApi.testSettings(payload);

      setTestResult(response.data);
      if (response.data.success) {
        setSuccess(response.data.message);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Connection test failed",
      );
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = {
        webhook_url: form.webhook_url.trim() || null,
        is_active: form.is_active,
      };
      if (form.webhook_token.trim()) {
        payload.webhook_token = form.webhook_token.trim();
      } else if (!settings?.token_set) {
        setError(
          "Generate or enter a webhook token before saving (required for X-API-KEY).",
        );
        setSaving(false);
        return;
      }

      const response = await wholesaleOrdersApi.updateSettings(payload);
      setSettings(response.data);
      setForm((prev) => ({ ...prev, webhook_token: "" }));
      setShowToken(false);
      setTokenGenerated(false);
      setSuccess("n8n parse webhook settings saved.");
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to save settings",
      );
    } finally {
      setSaving(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: s?.card2 || undefined,
      borderRadius: "8px",
      fontSize: 13.5,
      "& fieldset": { borderColor: s?.border },
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography
        sx={{
          fontSize: { xs: 24, md: 28 },
          fontWeight: 600,
          letterSpacing: "-0.4px",
          color: s?.text,
          mb: 0.75,
        }}
      >
        Order file parse (n8n)
      </Typography>
      <Typography
        sx={{ color: s?.muted, fontSize: 13, mb: 2.5, maxWidth: 640 }}
      >
        When creating an order, an uploaded invoice/order file is POSTed to this
        webhook with header <code>X-API-KEY</code>. n8n must return JSON{" "}
        <code>{`{ "items": [ { "title": "...", "qty_ordered": 1, ... } ] }`}</code>
        . Test hits the <code>webhook-test</code> URL; success is any HTTP
        status below 400 (response body optional).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Box
        sx={{
          border: `1px solid ${s?.border || "#e3e8f2"}`,
          borderRadius: "12px",
          background: s?.card || "#fff",
          p: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <HubIcon color="primary" />
          <Typography sx={{ fontWeight: 600, color: s?.text }}>
            Workflow configuration
          </Typography>
          {settings && (
            <Chip
              size="small"
              color={settings.configured ? "success" : "warning"}
              label={settings.configured ? "Configured" : "Not configured"}
            />
          )}
        </Box>

        <TextField
          fullWidth
          size="small"
          label="Parse webhook URL (production)"
          placeholder="https://n8n.example.com/webhook/..."
          value={form.webhook_url}
          onChange={(e) => {
            setForm((prev) => ({ ...prev, webhook_url: e.target.value }));
            setTestResult(null);
          }}
          helperText={
            toTestWebhookUrl(form.webhook_url)
              ? `Test URL: ${toTestWebhookUrl(form.webhook_url)}`
              : "Must contain /webhook/ so the test URL can be derived"
          }
          sx={{ mb: 2, ...inputSx }}
        />

        <TextField
          fullWidth
          size="small"
          label="Webhook token (X-API-KEY)"
          type={showToken ? "text" : "password"}
          value={form.webhook_token}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, webhook_token: e.target.value }))
          }
          placeholder={
            settings?.token_set
              ? "Token is set — enter again only to replace"
              : "Generate or paste token"
          }
          helperText={
            tokenGenerated
              ? "Copy this token into n8n Header Auth before saving."
              : settings?.token_set
                ? "A token is already stored (not shown). Leave blank to keep it — Test uses the saved token."
                : "Required. n8n Header Auth name must be X-API-KEY."
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {form.webhook_token && (
                  <Tooltip title="Copy">
                    <IconButton size="small" onClick={handleCopyToken}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Button
                  size="small"
                  onClick={() => setShowToken((v) => !v)}
                  sx={{ textTransform: "none", minWidth: 0 }}
                >
                  {showToken ? "Hide" : "Show"}
                </Button>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5, ...inputSx }}
        />

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={
              generatingToken ? (
                <CircularProgress size={16} />
              ) : (
                <AutorenewIcon />
              )
            }
            onClick={handleGenerateToken}
            disabled={generatingToken}
            sx={{ textTransform: "none" }}
          >
            Generate token
          </Button>
          <Button
            variant="outlined"
            startIcon={
              testing ? <CircularProgress size={16} /> : <PlayArrowIcon />
            }
            onClick={handleTestConnection}
            disabled={testing || !canTest}
            sx={{ textTransform: "none" }}
          >
            Test connection
          </Button>
          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: "none", ml: { sm: "auto" } }}
          >
            Save settings
          </Button>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={form.is_active}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, is_active: e.target.checked }))
              }
            />
          }
          label="Active (parse uses this webhook when creating orders)"
        />

        {testResult && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: "8px",
              background: s?.boxBg || "#f7f9fc",
              border: `1px solid ${s?.border || "#e3e8f2"}`,
              fontSize: 12.5,
            }}
          >
            <Typography sx={{ fontWeight: 600, mb: 0.5, color: s?.text }}>
              Test result
            </Typography>
            <Typography sx={{ color: s?.muted, fontSize: 12.5 }}>
              HTTP {testResult.webhook_status_code ?? "?"}
              {testResult.success ? " · OK" : ""}
            </Typography>
            <Typography sx={{ color: s?.muted, fontSize: 12, mt: 0.5 }}>
              {testResult.test_webhook_url}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CredentialsTab;
